"use client"

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { KeyRound, Banknote, Play, Users, UserPlus, AlertTriangle, RotateCcw } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useStore, selectLastClosedShift } from '@/lib/store'
import { toast } from 'sonner'
import { leftInDrawer } from '@/lib/drawer-float'
import { useT } from '@/lib/i18n'
import { rich } from '@/lib/i18n/rich'

interface Props {
  open: boolean
  onClose: () => void
}

export function OpenShiftDialog({ open, onClose }: Props) {
  const t = useT()
  const { cashiers, openShiftWithPin } = useStore()
  const router = useRouter()
  const [pin, setPin] = useState('')
  const [openingCash, setOpeningCash] = useState('')
  const [confirmEmpty, setConfirmEmpty] = useState(false)
  const [saving, setSaving] = useState(false)

  /**
   * The float carries over: what was counted into the drawer at last close is
   * what the next cashier finds in it. So the right number is already known
   * and does not need typing — only correcting, on the mornings the owner took
   * the money home. Leaving the field blank used to mean ₾0, and ₾0 is a
   * number no shop opens with, because it cannot give change for a ₾10 note.
   *
   * Branch matters here: `selectLastClosedShift` is branch-aware, so a till at
   * one branch never inherits another branch's float.
   */
  const lastClosed = useStore(selectLastClosedShift)
  // 119 — what the last close LEFT (its float), not what it counted: the
  // rest was taken at close, with a reason, and is not in the drawer.
  const carriedOver = lastClosed ? leftInDrawer(lastClosed) : null

  useEffect(() => {
    if (!open) return
    setPin('')
    setConfirmEmpty(false)
    setOpeningCash(carriedOver && carriedOver > 0 ? String(carriedOver) : '')
  }, [open, carriedOver])

  // A brand-new store has no cashiers yet — you can't open a shift until one
  // exists in the "თანამშრომლები" module. (This PIN is the CASHIER PIN, which
  // is different from the system lock PIN in Settings — a common mix-up.)
  // 1.67 — a waiter's PIN opens no shift (193), so a shop with only waiters has no cashier yet
  const activeCashiers = cashiers.filter(c => c.active && c.kind !== 'waiter')
  const noCashiers = activeCashiers.length === 0

  /**
   * The PIN identifies the cashier — on the SERVER since 116. The till used
   * to hold every cashier's PIN and match here, which let anyone read them
   * or open a shift in someone else's name. Now the PIN is sent, the server
   * answers with the shift (and the cashier's name) or refuses and counts
   * the miss; branch and one-drawer-per-person are its words too.
   * 4..8 — new PINs are six digits (058), older ones may be four.
   */
  const canOpen = pin.length >= 4

  const amount = Math.max(0, parseFloat(openingCash) || 0)

  const handleOpen = async () => {
    if (!canOpen) { toast.error(t('shift.enterPin')); return }
    /**
     * ₾0 is legitimate — a branch nobody has floated yet, today's `test#1` —
     * so it is confirmed, never blocked. Blocking would push a cashier into
     * typing a number that is not in the drawer, and an invented float is
     * worse than an honest zero: it looks true and it moves the difference.
     *
     * But it is never the right DEFAULT, and it used to be reachable by
     * leaving a field alone. `expected_cash` at close is opening + takings
     * (lib/cash.ts), so a float that is ₾50 short reports the cashier ₾50
     * over, every single day, on the one number the shift close exists for.
     */
    if (amount === 0 && !confirmEmpty) { setConfirmEmpty(true); return }
    setSaving(true)
    const shift = await openShiftWithPin(pin, amount)
    setSaving(false)
    if (shift) { toast.success(t('shift.opened', { name: shift.cashier_name ?? '' })); onClose() }
    else setPin('')   // refused: type it again (and a wrong one is not re-sent)
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-sm rounded-[24px] p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-black flex items-center gap-2">
            <Play className="size-5 text-emerald-500" /> {t('shift.open')}
          </DialogTitle>
          <DialogDescription>
            {noCashiers
              ? t('shift.needCashier')
              : t('shift.openHint')}
          </DialogDescription>
        </DialogHeader>

        {noCashiers ? (
          /* New store: no cashiers exist yet — guide the owner to create one.
             The shift PIN is the cashier's PIN (set per-cashier in Staff), NOT
             the system lock PIN from Settings. */
          <div className="flex flex-col items-center text-center gap-4 py-6">
            <div className="size-14 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/25 flex items-center justify-center">
              <Users className="size-7 text-amber-500" />
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-sm font-bold text-foreground">{t('shift.noCashiers')}</p>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-[280px]">
                {rich(t('shift.noCashiersBody'), { cashier: <strong className="text-foreground">{t('shift.cashierGen')}</strong> })}
              </p>
            </div>
            <Button
              onClick={() => { onClose(); router.push('/staff') }}
              className="rounded-xl h-11 px-6 font-bold"
            >
              <UserPlus className="size-4 mr-1.5" /> {t('shift.toStaff')}
            </Button>
          </div>
        ) : (
        <div className="flex flex-col gap-3.5 py-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-bold text-muted-foreground uppercase tracking-wide pl-1">{t('shift.cashierPin')} <span className="text-red-500">*</span></label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              {/* An in-app PIN, not the account password. With autoComplete="off" Chrome
                  still filled the saved login password, and the digit filter kept its
                  digits — the owner saw „• •" in a fresh dialog for months (2026-10-14).
                  "new-password" + an own name keeps the browser out, as the lock screen does. */}
              <Input
                type="password" inputMode="numeric" autoComplete="new-password" name="no-autofill-shift-pin" maxLength={8} autoFocus
                className="h-12 pl-9 rounded-xl font-mono tracking-[0.5em] text-xl text-center"
                placeholder="••••"
                value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-bold text-muted-foreground uppercase tracking-wide pl-1">
              {t('shift.openingCash')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                type="number" min="0" inputMode="decimal"
                className="h-11 pl-9 rounded-xl" placeholder="0.00"
                value={openingCash}
                onChange={e => { setOpeningCash(e.target.value); setConfirmEmpty(false) }}
              />
            </div>

            {/* Where the number came from, so an owner who emptied the drawer
                overnight knows it is a carried-over default and not a fact. */}
            {carriedOver !== null && carriedOver > 0 && (
              <div className="flex items-center justify-between gap-2 pl-1">
                <p className="text-[12px] text-muted-foreground">
                  {rich(t('shift.carried'), { sum: <strong className="text-foreground tabular-nums">₾{carriedOver.toFixed(2)}</strong> })}
                </p>
                {amount !== carriedOver && (
                  <button
                    type="button"
                    onClick={() => { setOpeningCash(String(carriedOver)); setConfirmEmpty(false) }}
                    className="press flex items-center gap-1 text-[12px] font-bold text-primary hover:underline shrink-0"
                  >
                    <RotateCcw className="size-3" /> {t('common.return')}
                  </button>
                )}
              </div>
            )}

            {/* 119 — the database records what the previous shift left
                (opening_carried); a different float is a gap the Z-report,
                the journal and the owner's morning mail all show. Said here,
                before it is typed into history. */}
            {carriedOver !== null && Math.abs(amount - carriedOver) >= 0.005 ? (
              <p className="text-[12px] leading-relaxed text-amber-800 dark:text-amber-300 bg-amber-500/10 rounded-lg px-2.5 py-1.5">
                {amount < carriedOver
                  ? t('shift.gapLess', { sum: `₾${(carriedOver - amount).toFixed(2)}` })
                  : t('shift.gapMore', { sum: `₾${(amount - carriedOver).toFixed(2)}` })}
                {' '}{t('shift.gapNote')}
              </p>
            ) : (
              <p className="text-[12px] text-muted-foreground pl-1">
                {t('shift.wrongFloat')}
              </p>
            )}

            {confirmEmpty && (
              <div className="flex items-start gap-2.5 rounded-2xl bg-amber-500/10 border border-amber-500/25 p-3 mt-1">
                <AlertTriangle className="size-4 shrink-0 text-amber-600 dark:text-amber-500 mt-0.5" />
                <p className="text-[12px] leading-relaxed text-amber-900 dark:text-amber-200">
                  <span className="font-black">{t('shift.emptyQ')}</span> {t('shift.emptyBody')}
                </p>
              </div>
            )}
          </div>
        </div>
        )}
        {!noCashiers && (
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" className="rounded-xl h-11 px-5 font-bold text-muted-foreground" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
            <Button
              onClick={handleOpen}
              disabled={saving || !canOpen}
              className={confirmEmpty
                ? "rounded-xl h-11 px-7 bg-amber-500 hover:bg-amber-600 text-white border-0 font-black"
                : "rounded-xl h-11 px-7 bg-emerald-600 hover:bg-emerald-700 text-white border-0 font-black"}
            >
              <Play className="size-4 mr-1" /> {confirmEmpty ? t('shift.yesEmpty') : t('shift.open')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
